import {
  FlowchartDirection,
  FlowchartEdgeKind,
  FlowchartLabelOrigin,
  FlowchartMarkerKind,
} from './flowchart-parser-model';
import { getMatchGroup, getOptionalMatchGroup, stripLabelQuotes } from './flowchart-parser-labels';
import {
  parseNodeExpression,
  type ParsedNodeReference,
} from './flowchart-parser-node-expression';

enum ScanTokenKind {
  NodeRef = 'nodeRef',
  Operator = 'operator',
  Ampersand = 'ampersand',
}

export interface ParsedEdge {
  edgeId: string | null;
  from: ParsedNodeReference;
  to: ParsedNodeReference;
  label: string | null;
  kind: FlowchartEdgeKind;
  startMarker: FlowchartMarkerKind;
  endMarker: FlowchartMarkerKind;
}

export interface ParsedEdgeChain {
  refs: ParsedNodeReference[];
  edges: ParsedEdge[];
}

interface OperatorToken {
  kind: ScanTokenKind.Operator;
  consumed: number;
  edgeId: string | null;
  label: string | null;
  edgeKind: FlowchartEdgeKind;
  startMarker: FlowchartMarkerKind;
  endMarker: FlowchartMarkerKind;
}

interface NodeRefToken {
  kind: ScanTokenKind.NodeRef;
  value: string;
}

interface AmpersandToken {
  kind: ScanTokenKind.Ampersand;
}

type ScanToken = NodeRefToken | OperatorToken | AmpersandToken;

interface EdgeOperatorCore {
  consumed: number;
  label: string | null;
  edgeKind: FlowchartEdgeKind;
  endMarker: FlowchartMarkerKind;
}

interface OperatorParseResult {
  consumed: number;
  edgeId: string | null;
  label: string | null;
  edgeKind: FlowchartEdgeKind;
  startMarker: FlowchartMarkerKind;
  endMarker: FlowchartMarkerKind;
}

interface LabeledEndResult {
  consumed: number;
  label: string;
  endMarker: FlowchartMarkerKind;
  edgeKind: FlowchartEdgeKind;
}

enum ScannerQuoteMode {
  None = 'none',
  DoubleQuote = 'doubleQuote',
  Backtick = 'backtick',
}

type ScannerDepthKey = 'squareDepth' | 'roundDepth' | 'braceDepth';

interface ScannerSyntaxState {
  squareDepth: number;
  roundDepth: number;
  braceDepth: number;
  quoteMode: ScannerQuoteMode;
}

interface ScannerDepthDelta {
  key: ScannerDepthKey;
  amount: 1 | -1;
}

interface LabeledEndCandidate {
  consumed: number;
  endMarker: FlowchartMarkerKind;
}

interface ScanBranchInput {
  line: string;
  cursor: number;
  char: string;
  buffer: string;
  tokens: readonly ScanToken[];
}

interface ScanBranchAtInput extends ScanBranchInput {
  syntaxState: ScannerSyntaxState;
}

interface ScanBranchResult {
  cursor: number;
  buffer: string;
  tokens: ScanToken[];
}

interface ParsedEdgeBuildInput {
  refsByGroup: readonly ParsedNodeReference[][];
  operator: OperatorToken;
  operatorIndex: number;
}

interface OperatorPrefix {
  edgeId: string | null;
  localOffset: number;
  startMarker: FlowchartMarkerKind;
}

interface EdgeIdNodePrefix {
  edgeId: string;
  nodeRef: string;
}

type ParsedNodeReferenceCandidate = ParsedNodeReference | null;
type ScanBranchHandler = (input: ScanBranchInput) => ScanBranchResult | null;

const FLOWCHART_HEADER_REGEX = /^(?:flowchart|graph)\s*(TD|TB|LR|RL|BT)?\b/i;
const EDGE_ID_PREFIX_REGEX = /^([A-Za-z_][A-Za-z0-9_-]*)@/;
const EDGE_ID_NODE_PREFIX_REGEX = /^([A-Za-z_][A-Za-z0-9_-]*)@(.+)$/;

const DIRECTION_MAP: Record<string, FlowchartDirection> = {
  TD: FlowchartDirection.TopToBottom,
  TB: FlowchartDirection.TopToBottom,
  LR: FlowchartDirection.LeftToRight,
  RL: FlowchartDirection.RightToLeft,
  BT: FlowchartDirection.BottomToTop,
};

const EDGE_KIND_WITH_ARROW_MARKER: Readonly<Record<FlowchartEdgeKind, FlowchartEdgeKind>> = {
  [FlowchartEdgeKind.Arrow]: FlowchartEdgeKind.Arrow,
  [FlowchartEdgeKind.Dotted]: FlowchartEdgeKind.Dotted,
  [FlowchartEdgeKind.Open]: FlowchartEdgeKind.Arrow,
  [FlowchartEdgeKind.Thick]: FlowchartEdgeKind.Thick,
};

const SCANNER_INITIAL_STATE: ScannerSyntaxState = {
  squareDepth: 0,
  roundDepth: 0,
  braceDepth: 0,
  quoteMode: ScannerQuoteMode.None,
};

const QUOTE_MODE_BY_START_CHAR: Readonly<Record<string, ScannerQuoteMode>> = {
  '"': ScannerQuoteMode.DoubleQuote,
  '`': ScannerQuoteMode.Backtick,
};

const QUOTE_END_CHAR_BY_MODE: Readonly<Record<ScannerQuoteMode, string | null>> = {
  [ScannerQuoteMode.None]: null,
  [ScannerQuoteMode.DoubleQuote]: '"',
  [ScannerQuoteMode.Backtick]: '`',
};

const SCANNER_DEPTH_DELTAS: Readonly<Record<string, ScannerDepthDelta>> = {
  '[': { key: 'squareDepth', amount: 1 },
  ']': { key: 'squareDepth', amount: -1 },
  '(': { key: 'roundDepth', amount: 1 },
  ')': { key: 'roundDepth', amount: -1 },
  '{': { key: 'braceDepth', amount: 1 },
  '}': { key: 'braceDepth', amount: -1 },
};

export function sanitizeMermaidLine(line: string): string {
  return stripCommentOutsideSyntax(line).trim().replace(/;\s*$/, '').trim();
}

export function parseHeaderDirection(headerLine: string): FlowchartDirection {
  const directionToken = headerLine.match(FLOWCHART_HEADER_REGEX)?.[1]?.toUpperCase() ?? '';
  return DIRECTION_MAP[directionToken] ?? FlowchartDirection.TopToBottom;
}

function nextScannerQuoteMode(input: {
  state: ScannerSyntaxState;
  char: string;
}): ScannerQuoteMode {
  const activeEndChar = QUOTE_END_CHAR_BY_MODE[input.state.quoteMode];
  if (activeEndChar) {
    return input.char === activeEndChar ? ScannerQuoteMode.None : input.state.quoteMode;
  }
  return QUOTE_MODE_BY_START_CHAR[input.char] ?? ScannerQuoteMode.None;
}

function applyScannerDepthDelta(input: {
  state: ScannerSyntaxState;
  delta: ScannerDepthDelta;
}): ScannerSyntaxState {
  const currentDepth = input.state[input.delta.key];
  const nextDepth =
    input.delta.amount > 0 ? currentDepth + 1 : Math.max(0, currentDepth - 1);
  return { ...input.state, [input.delta.key]: nextDepth };
}

function advanceScannerSyntaxState(input: {
  state: ScannerSyntaxState;
  char: string;
}): ScannerSyntaxState {
  const quoteMode = nextScannerQuoteMode(input);
  if (input.state.quoteMode !== ScannerQuoteMode.None || quoteMode !== ScannerQuoteMode.None) {
    return { ...input.state, quoteMode };
  }

  const delta = SCANNER_DEPTH_DELTAS[input.char];
  return delta ? applyScannerDepthDelta({ state: input.state, delta }) : input.state;
}

function isScannerAtTopLevel(state: ScannerSyntaxState): boolean {
  return (
    state.quoteMode === ScannerQuoteMode.None &&
    state.squareDepth === 0 &&
    state.roundDepth === 0 &&
    state.braceDepth === 0
  );
}

function isTopLevelCommentStart(input: {
  state: ScannerSyntaxState;
  char: string;
  next: string | undefined;
}): boolean {
  return isScannerAtTopLevel(input.state) && input.char === '%' && input.next === '%';
}

function stripCommentOutsideSyntax(line: string): string {
  let syntaxState = SCANNER_INITIAL_STATE;

  for (let index = 0; index < line.length; index += 1) {
    const char = line[index] ?? '';
    const next = line[index + 1];
    syntaxState = advanceScannerSyntaxState({ state: syntaxState, char });
    if (isTopLevelCommentStart({ state: syntaxState, char, next })) {
      return line.slice(0, index);
    }
  }

  return line;
}

function markerFromChar(char: string | undefined, arrowChar: string): FlowchartMarkerKind {
  if (char === arrowChar) return FlowchartMarkerKind.Arrow;
  if (char === 'o') return FlowchartMarkerKind.Circle;
  if (char === 'x') return FlowchartMarkerKind.Cross;
  return FlowchartMarkerKind.None;
}

function getStartMarker(char: string | undefined): FlowchartMarkerKind {
  return markerFromChar(char, '<');
}

function getEndMarker(char: string | undefined): FlowchartMarkerKind {
  return markerFromChar(char, '>');
}

function isStartMarkerChar(char: string | undefined): boolean {
  return char === '<' || char === 'o' || char === 'x';
}

function isEndMarkerChar(char: string | undefined): boolean {
  return char === '>' || char === 'o' || char === 'x';
}

function attachTrailingPipeLabel(input: {
  source: string;
  consumed: number;
  currentLabel: string | null;
}): { consumed: number; label: string | null } {
  const { source, consumed, currentLabel } = input;
  let cursor = consumed;
  while (source[cursor] === ' ') {
    cursor += 1;
  }
  if (source[cursor] !== '|') {
    return { consumed, label: currentLabel };
  }

  const endIndex = source.indexOf('|', cursor + 1);
  if (endIndex < 0) {
    return { consumed, label: currentLabel };
  }

  const rawLabel = source.slice(cursor + 1, endIndex);
  return {
    consumed: endIndex + 1,
    label: stripLabelQuotes(rawLabel),
  };
}

function scanForLabeledEnd(input: {
  source: string;
  fromIndex: number;
  closing: string;
  baseKind: FlowchartEdgeKind;
}): LabeledEndResult | null {
  const { source, fromIndex, closing, baseKind } = input;
  let syntaxState = SCANNER_INITIAL_STATE;

  for (let index = fromIndex; index < source.length; index += 1) {
    const char = source[index] ?? '';
    syntaxState = advanceScannerSyntaxState({ state: syntaxState, char });
    const candidate = readLabeledEndAt({
      source,
      index,
      closing,
      baseKind,
      syntaxState,
    });
    if (!candidate) {
      continue;
    }

    const label = stripLabelQuotes(source.slice(fromIndex, index));
    return {
      consumed: candidate.consumed,
      label,
      endMarker: candidate.endMarker,
      edgeKind: getLabeledEndEdgeKind({ baseKind, endMarker: candidate.endMarker }),
    };
  }

  return null;
}

function readLabeledEndAt(input: {
  source: string;
  index: number;
  closing: string;
  baseKind: FlowchartEdgeKind;
  syntaxState: ScannerSyntaxState;
}): LabeledEndCandidate | null {
  if (!isScannerAtTopLevel(input.syntaxState)) {
    return null;
  }
  if (!input.source.startsWith(input.closing, input.index)) {
    return null;
  }
  return readLabeledEndCandidate(input);
}

function readLabeledEndCandidate(input: {
  source: string;
  index: number;
  closing: string;
  baseKind: FlowchartEdgeKind;
}): LabeledEndCandidate | null {
  const markerChar = input.source[input.index + input.closing.length];
  if (isEndMarkerChar(markerChar)) {
    return {
      consumed: input.index + input.closing.length + 1,
      endMarker: getEndMarker(markerChar),
    };
  }
  if (input.baseKind === FlowchartEdgeKind.Open && markerChar === '-') {
    return {
      consumed: input.index + input.closing.length + 1,
      endMarker: FlowchartMarkerKind.None,
    };
  }
  return null;
}

function getLabeledEndEdgeKind(input: {
  baseKind: FlowchartEdgeKind;
  endMarker: FlowchartMarkerKind;
}): FlowchartEdgeKind {
  if (input.endMarker === FlowchartMarkerKind.Arrow || input.baseKind === FlowchartEdgeKind.Arrow) {
    return input.baseKind;
  }
  return FlowchartEdgeKind.Open;
}

function parseDashOperator(source: string, offset: number): EdgeOperatorCore | null {
  if (!source.startsWith('--', offset)) {
    return null;
  }

  const markerChar = source[offset + 2];
  if (isEndMarkerChar(markerChar)) {
    const endMarker = getEndMarker(markerChar);
    const pipe = attachTrailingPipeLabel({
      source,
      consumed: offset + 3,
      currentLabel: null,
    });
    return {
      consumed: pipe.consumed,
      label: pipe.label,
      edgeKind:
        endMarker === FlowchartMarkerKind.Arrow
          ? FlowchartEdgeKind.Arrow
          : FlowchartEdgeKind.Open,
      endMarker,
    };
  }

  if (markerChar === '-') {
    const pipe = attachTrailingPipeLabel({
      source,
      consumed: offset + 3,
      currentLabel: null,
    });
    return {
      consumed: pipe.consumed,
      label: pipe.label,
      edgeKind: FlowchartEdgeKind.Open,
      endMarker: FlowchartMarkerKind.None,
    };
  }

  const labeled = scanForLabeledEnd({
    source,
    fromIndex: offset + 2,
    closing: '--',
    baseKind: FlowchartEdgeKind.Arrow,
  });
  return labeled;
}

function parseThickOperator(source: string, offset: number): EdgeOperatorCore | null {
  if (!source.startsWith('==', offset)) {
    return null;
  }

  if (source[offset + 2] === '>') {
    const pipe = attachTrailingPipeLabel({
      source,
      consumed: offset + 3,
      currentLabel: null,
    });
    return {
      consumed: pipe.consumed,
      label: pipe.label,
      edgeKind: FlowchartEdgeKind.Thick,
      endMarker: FlowchartMarkerKind.Arrow,
    };
  }

  return scanForLabeledEnd({
    source,
    fromIndex: offset + 2,
    closing: '==',
    baseKind: FlowchartEdgeKind.Thick,
  });
}

function parseDottedOperator(source: string, offset: number): EdgeOperatorCore | null {
  if (!source.startsWith('-.', offset)) {
    return null;
  }

  if (source.startsWith('-.->', offset)) {
    const pipe = attachTrailingPipeLabel({
      source,
      consumed: offset + 4,
      currentLabel: null,
    });
    return {
      consumed: pipe.consumed,
      label: pipe.label,
      edgeKind: FlowchartEdgeKind.Dotted,
      endMarker: FlowchartMarkerKind.Arrow,
    };
  }

  return scanForLabeledEnd({
    source,
    fromIndex: offset + 2,
    closing: '.-',
    baseKind: FlowchartEdgeKind.Dotted,
  });
}

function parseOperatorCore(source: string, offset: number): EdgeOperatorCore | null {
  return (
    parseDottedOperator(source, offset) ??
    parseThickOperator(source, offset) ??
    parseDashOperator(source, offset)
  );
}

function readOperatorEdgeId(input: {
  slice: string;
}): { edgeId: string | null; localOffset: number } {
  const edgeIdMatch = input.slice.match(EDGE_ID_PREFIX_REGEX);
  return {
    edgeId: getOptionalMatchGroup({ match: edgeIdMatch, index: 1 }),
    localOffset: edgeIdMatch ? edgeIdMatch[0].length : 0,
  };
}

function readOptionalStartMarker(char: string | undefined): FlowchartMarkerKind {
  return isStartMarkerChar(char) ? getStartMarker(char) : FlowchartMarkerKind.None;
}

function readOperatorPrefix(slice: string): OperatorPrefix {
  const edgeIdPrefix = readOperatorEdgeId({ slice });
  const startMarker = readOptionalStartMarker(slice[edgeIdPrefix.localOffset]);
  const markerOffset = startMarker !== FlowchartMarkerKind.None ? 1 : 0;
  return {
    edgeId: edgeIdPrefix.edgeId,
    localOffset: edgeIdPrefix.localOffset + markerOffset,
    startMarker,
  };
}

function hasArrowMarker(input: {
  startMarker: FlowchartMarkerKind;
  endMarker: FlowchartMarkerKind;
}): boolean {
  return (
    input.startMarker === FlowchartMarkerKind.Arrow ||
    input.endMarker === FlowchartMarkerKind.Arrow
  );
}

function normalizeOperatorEdgeKind(input: {
  coreKind: FlowchartEdgeKind;
  startMarker: FlowchartMarkerKind;
  endMarker: FlowchartMarkerKind;
}): FlowchartEdgeKind {
  return hasArrowMarker(input)
    ? EDGE_KIND_WITH_ARROW_MARKER[input.coreKind]
    : input.coreKind;
}

function normalizeOperatorLabel(label: string | null): string | null {
  if (!label) {
    return null;
  }
  const normalized = stripLabelQuotes(label);
  return normalized.length > 0 ? normalized : null;
}

function buildOperatorParseResult(input: {
  prefix: OperatorPrefix;
  core: EdgeOperatorCore;
}): OperatorParseResult {
  return {
    consumed: input.core.consumed,
    edgeId: input.prefix.edgeId,
    label: normalizeOperatorLabel(input.core.label),
    edgeKind: normalizeOperatorEdgeKind({
      coreKind: input.core.edgeKind,
      startMarker: input.prefix.startMarker,
      endMarker: input.core.endMarker,
    }),
    startMarker: input.prefix.startMarker,
    endMarker: input.core.endMarker,
  };
}

function parseOperatorAt(source: string, cursor: number): OperatorParseResult | null {
  const slice = source.slice(cursor);
  const prefix = readOperatorPrefix(slice);
  const core = parseOperatorCore(slice, prefix.localOffset);
  return core ? buildOperatorParseResult({ prefix, core }) : null;
}

function parseEdgeIdNodePrefix(firstNodeRef: string): EdgeIdNodePrefix | null {
  const edgeIdMatch = firstNodeRef.match(EDGE_ID_NODE_PREFIX_REGEX);
  if (!edgeIdMatch) {
    return null;
  }

  const edgeId = getMatchGroup({ match: edgeIdMatch, index: 1 }) ?? '';
  const nodeRef = (getMatchGroup({ match: edgeIdMatch, index: 2 }) ?? '').trim();
  return edgeId && nodeRef ? { edgeId, nodeRef } : null;
}

function readPrefixedEdgeIdCandidate(input: {
  groups: string[][];
  operators: OperatorToken[];
}): EdgeIdNodePrefix | null {
  const [firstGroup] = input.groups;
  const [firstOperator] = input.operators;
  const [firstNodeRef] = firstGroup ?? [];
  if (!firstGroup || !firstOperator || firstOperator.edgeId || !firstNodeRef) {
    return null;
  }

  return parseEdgeIdNodePrefix(firstNodeRef);
}

function applyPrefixedEdgeId(input: {
  groups: string[][];
  operators: OperatorToken[];
  prefix: EdgeIdNodePrefix;
}): { groups: string[][]; operators: OperatorToken[] } {
  const [firstGroup = []] = input.groups;
  const [firstOperator] = input.operators;
  return {
    groups: [[input.prefix.nodeRef, ...firstGroup.slice(1)], ...input.groups.slice(1)],
    operators: firstOperator
      ? [{ ...firstOperator, edgeId: input.prefix.edgeId }, ...input.operators.slice(1)]
      : input.operators,
  };
}

function extractPrefixedEdgeId(input: {
  groups: string[][];
  operators: OperatorToken[];
}): { groups: string[][]; operators: OperatorToken[] } {
  const prefix = readPrefixedEdgeIdCandidate(input);
  return prefix ? applyPrefixedEdgeId({ ...input, prefix }) : input;
}

function flushNodeToken(tokens: readonly ScanToken[], rawValue: string): ScanToken[] {
  const value = rawValue.trim();
  if (!value) {
    return [...tokens];
  }
  return [...tokens, { kind: ScanTokenKind.NodeRef, value }];
}

function scanAmpersandBranch(input: ScanBranchInput): ScanBranchResult | null {
  if (input.char !== '&') {
    return null;
  }
  return {
    cursor: input.cursor + 1,
    buffer: '',
    tokens: [...flushNodeToken(input.tokens, input.buffer), { kind: ScanTokenKind.Ampersand }],
  };
}

function scanOperatorBranch(input: ScanBranchInput): ScanBranchResult | null {
  const operator = parseOperatorAt(input.line, input.cursor);
  if (!operator) {
    return null;
  }

  return {
    cursor: input.cursor + operator.consumed,
    buffer: '',
    tokens: [
      ...flushNodeToken(input.tokens, input.buffer),
      {
        kind: ScanTokenKind.Operator,
        consumed: operator.consumed,
        edgeId: operator.edgeId,
        label: operator.label,
        edgeKind: operator.edgeKind,
        startMarker: operator.startMarker,
        endMarker: operator.endMarker,
      },
    ],
  };
}

const TOP_LEVEL_SCAN_BRANCHES: ReadonlyArray<ScanBranchHandler> = [
  scanAmpersandBranch,
  scanOperatorBranch,
];

function applyTopLevelScanBranch(input: ScanBranchInput): ScanBranchResult | null {
  return TOP_LEVEL_SCAN_BRANCHES.reduce<ScanBranchResult | null>(
    (matched, branch) => matched || branch(input),
    null,
  );
}

function readScanBranchAt(input: ScanBranchAtInput): ScanBranchResult | null {
  if (!isScannerAtTopLevel(input.syntaxState)) {
    return null;
  }
  return applyTopLevelScanBranch(input);
}

function tokensWithOperator(tokens: ScanToken[]): ScanToken[] | null {
  return tokens.some((token) => token.kind === ScanTokenKind.Operator) ? tokens : null;
}

function scanStatement(line: string): ScanToken[] | null {
  let syntaxState = SCANNER_INITIAL_STATE;
  let buffer = '';
  let tokens: ScanToken[] = [];
  let cursor = 0;

  while (cursor < line.length) {
    const char = line[cursor] ?? '';
    syntaxState = advanceScannerSyntaxState({ state: syntaxState, char });
    const branchResult = readScanBranchAt({ line, cursor, char, buffer, tokens, syntaxState });
    if (branchResult) {
      tokens = branchResult.tokens;
      buffer = branchResult.buffer;
      cursor = branchResult.cursor;
      continue;
    }

    buffer += char;
    cursor += 1;
  }

  tokens = flushNodeToken(tokens, buffer);
  return tokensWithOperator(tokens);
}

function splitIntoNodeGroups(tokens: readonly ScanToken[]): {
  groups: string[][];
  operators: OperatorToken[];
} | null {
  const groups: string[][] = [];
  const operators: OperatorToken[] = [];
  let currentGroup: string[] = [];

  for (const token of tokens) {
    if (token.kind === ScanTokenKind.NodeRef) {
      currentGroup = [...currentGroup, token.value];
      continue;
    }
    if (token.kind === ScanTokenKind.Ampersand) {
      continue;
    }
    if (currentGroup.length === 0) {
      return null;
    }
    groups.push(currentGroup);
    operators.push(token);
    currentGroup = [];
  }

  if (currentGroup.length === 0 || groups.length !== operators.length) {
    return null;
  }

  return extractPrefixedEdgeId({ groups: [...groups, currentGroup], operators });
}

function splitCompleteNodeGroups(tokens: readonly ScanToken[]): {
  groups: string[][];
  operators: OperatorToken[];
} | null {
  const grouped = splitIntoNodeGroups(tokens);
  if (!grouped) {
    return null;
  }
  if (grouped.groups.length !== grouped.operators.length + 1) {
    return null;
  }
  return grouped;
}

function parseNodeReferenceGroup(group: readonly string[]): ParsedNodeReferenceCandidate[] {
  return group.map((nodeRef) =>
    parseNodeExpression({
      expression: nodeRef,
      origin: FlowchartLabelOrigin.ImplicitEdgeRef,
    }),
  );
}

function hasUnparsedNodeReference(
  refsByGroup: ReadonlyArray<readonly ParsedNodeReferenceCandidate[]>,
): boolean {
  return refsByGroup.some((group) => group.some((nodeRef) => nodeRef == null));
}

function keepParsedNodeReferences(
  group: readonly ParsedNodeReferenceCandidate[],
): ParsedNodeReference[] {
  return group.filter((nodeRef): nodeRef is ParsedNodeReference => nodeRef != null);
}

function buildParsedEdges(input: ParsedEdgeBuildInput): ParsedEdge[] {
  const fromRefs = input.refsByGroup[input.operatorIndex] ?? [];
  const toRefs = input.refsByGroup[input.operatorIndex + 1] ?? [];
  return fromRefs.flatMap((fromRef) => {
    return toRefs.map<ParsedEdge>((toRef) => ({
      edgeId: input.operator.edgeId,
      from: fromRef,
      to: toRef,
      label: input.operator.label,
      kind: input.operator.edgeKind,
      startMarker: input.operator.startMarker,
      endMarker: input.operator.endMarker,
    }));
  });
}

export function parseEdgeChain(line: string): ParsedEdgeChain | null {
  const tokens = scanStatement(line);
  if (!tokens) {
    return null;
  }

  const grouped = splitCompleteNodeGroups(tokens);
  if (!grouped) {
    return null;
  }

  const refsByGroup = grouped.groups.map(parseNodeReferenceGroup);
  if (hasUnparsedNodeReference(refsByGroup)) {
    return null;
  }

  const parsedRefsByGroup = refsByGroup.map(keepParsedNodeReferences);
  const refs = parsedRefsByGroup.flat();
  const edges = grouped.operators.flatMap((operator, operatorIndex) =>
    buildParsedEdges({ refsByGroup: parsedRefsByGroup, operator, operatorIndex }),
  );

  return { refs, edges };
}
